import { useState } from "react";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Code2,
  Copy,
  Check,
  Terminal,
  Lock,
  Zap,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  KeyRound,
} from "lucide-react";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  {
    key: "auth",
    title: "Autenticación",
    icon: <Lock className="w-4 h-4" />,
    description:
      "Todos los endpoints (excepto /auth/login) requieren un JWT Bearer token en el header Authorization.",
    endpoints: [
      {
        method: "POST",
        path: "/api/auth/login",
        title: "Iniciar sesión",
        body: `{
  "email": "cliente@parthenon.cl",
  "password": "Cliente2026!"
}`,
        response: `{
  "token": "eyJhbGc...",
  "user": {
    "id": "...",
    "email": "cliente@parthenon.cl",
    "role": "client",
    "razon_social": "Parthenon Pharma SpA",
    "id_cliente": "PARTHENON"
  }
}`,
      },
      {
        method: "GET",
        path: "/api/auth/me",
        title: "Perfil actual",
        response: `{
  "id": "...",
  "email": "...",
  "role": "client",
  "razon_social": "Parthenon Pharma SpA"
}`,
      },
    ],
  },
  {
    key: "batches",
    title: "Envíos (XML/PDF)",
    icon: <Zap className="w-4 h-4" />,
    description:
      "Sube documentos tributarios (XML o PDF, o ZIP con varios) y consulta lotes procesados.",
    endpoints: [
      {
        method: "POST",
        path: "/api/batches/upload",
        title: "Subir XML/PDF por mes",
        multipart: `files=@guia.xml
mes=2026-04
descripcion=Envío abril
responsable=LUIS GUARDA`,
        response: `{
  "id": "...",
  "mes": "2026-04",
  "total_docs": 1,
  "products_summary": ["Brinzolamida / Timolol"]
}`,
      },
      {
        method: "GET",
        path: "/api/batches",
        title: "Listar mis envíos",
      },
      {
        method: "GET",
        path: "/api/batches/{batch_id}",
        title: "Detalle del envío",
      },
      {
        method: "GET",
        path: "/api/batches/{batch_id}/sap",
        title: "Descargar SAP xlsx",
        response: "→ Excel .xlsx con las 15 columnas SAP",
      },
    ],
  },
  {
    key: "rda",
    title: "RDA (Confirmaciones de entrega)",
    icon: <Terminal className="w-4 h-4" />,
    description:
      "Archivos Excel con confirmación de entrega. Subida es solo para admin, descarga para el cliente asociado.",
    endpoints: [
      {
        method: "POST",
        path: "/api/admin/clients/{user_id}/rda",
        title: "Subir RDA (admin)",
        multipart: `file=@rda.xlsx
mes=2026-04`,
      },
      {
        method: "GET",
        path: "/api/rda",
        title: "Listar RDAs (cliente ve los suyos, admin todos)",
      },
      {
        method: "GET",
        path: "/api/rda/{rda_id}/download",
        title: "Descargar RDA original",
      },
    ],
  },
  {
    key: "cedibles",
    title: "Cedibles (GD en PDF)",
    icon: <Code2 className="w-4 h-4" />,
    description:
      "Guías de despacho en PDF (copia CEDIBLE). Admin sube, cliente descarga.",
    endpoints: [
      {
        method: "POST",
        path: "/api/admin/clients/{user_id}/cedible",
        title: "Subir Cedibles (multipart, varios PDFs)",
        multipart: `files=@GD1008.pdf
files=@GD1009.pdf
mes=2026-04`,
      },
      {
        method: "GET",
        path: "/api/cedible",
        title: "Listar cedibles",
      },
      {
        method: "GET",
        path: "/api/cedible/{id}/download",
        title: "Descargar PDF",
      },
    ],
  },
  {
    key: "consolidado",
    title: "Consolidado SAP + RDA",
    icon: <Zap className="w-4 h-4" />,
    description:
      "Un Excel por cliente por mes que combina las 15 columnas SAP con 6 columnas RDA.",
    endpoints: [
      {
        method: "GET",
        path: "/api/consolidado/summary",
        title: "Resumen + meses disponibles (cliente)",
      },
      {
        method: "GET",
        path: "/api/consolidado/download?month=2026-04",
        title: "Descargar consolidado del mes",
      },
      {
        method: "GET",
        path: "/api/admin/consolidado",
        title: "Resumen de todos los clientes (admin)",
      },
      {
        method: "GET",
        path: "/api/admin/consolidado/{user_id}/download?month=YYYY-MM",
        title: "Descargar por cliente y mes (admin)",
      },
      {
        method: "GET",
        path: "/api/admin/consolidado/download-all?month=YYYY-MM",
        title: "ZIP con todos los clientes (admin)",
      },
    ],
  },
  {
    key: "admin",
    title: "Administración",
    icon: <KeyRound className="w-4 h-4" />,
    description: "Solo accesible con role=admin.",
    endpoints: [
      { method: "GET", path: "/api/admin/clients", title: "Lista de clientes + contadores" },
      { method: "GET", path: "/api/admin/clients/{user_id}/summary", title: "Perfil completo de un cliente" },
      { method: "GET", path: "/api/admin/users", title: "Todos los usuarios" },
      { method: "POST", path: "/api/admin/users", title: "Crear usuario", body: `{"email":"...","password":"...","name":"...","role":"client","rut":"...","razon_social":"...","id_cliente":"..."}` },
      { method: "GET", path: "/api/admin/stats", title: "KPIs generales" },
      { method: "GET", path: "/api/comunas", title: "Mapa comuna → región (Chile)" },
    ],
  },
];

function MethodBadge({ method }) {
  const colors = {
    GET: "bg-emerald-100 text-emerald-800",
    POST: "bg-blue-100 text-blue-800",
    PATCH: "bg-amber-100 text-amber-800",
    DELETE: "bg-red-100 text-red-800",
    PUT: "bg-indigo-100 text-indigo-800",
  };
  return (
    <span
      className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${colors[method] || "bg-slate-100 text-slate-800"}`}
    >
      {method}
    </span>
  );
}

function CopyButton({ text, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copiado");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={copy}
      data-testid={testid}
      className="p-1.5 hover:bg-slate-200 text-slate-500 transition-colors"
      title="Copiar"
    >
      {copied ? (
        <Check className="w-3.5 h-3.5 text-emerald-600" />
      ) : (
        <Copy className="w-3.5 h-3.5" />
      )}
    </button>
  );
}

function CodeBlock({ code, testid }) {
  return (
    <div className="relative bg-[#0B1B3D] text-slate-100 font-mono text-xs">
      <div className="absolute top-1.5 right-1.5">
        <CopyButton text={code} testid={testid} />
      </div>
      <pre className="p-4 overflow-x-auto whitespace-pre-wrap">{code}</pre>
    </div>
  );
}

function Endpoint({ ep, token }) {
  const [open, setOpen] = useState(false);
  const fullUrl = `${BASE_URL}${ep.path}`;
  const curl = ep.multipart
    ? `curl -X ${ep.method} '${fullUrl}' \\
  -H 'Authorization: Bearer ${token || "<TOKEN>"}' \\
  ${ep.multipart
    .split("\n")
    .map((line) => `-F '${line}'`)
    .join(" \\\n  ")}`
    : ep.body
    ? `curl -X ${ep.method} '${fullUrl}' \\
  -H 'Authorization: Bearer ${token || "<TOKEN>"}' \\
  -H 'Content-Type: application/json' \\
  -d '${ep.body.replace(/\n\s*/g, " ")}'`
    : `curl -X ${ep.method} '${fullUrl}' \\
  -H 'Authorization: Bearer ${token || "<TOKEN>"}'`;

  return (
    <div className="border border-slate-200 bg-white">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`endpoint-toggle-${ep.method}-${ep.path}`}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          {open ? (
            <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          )}
          <MethodBadge method={ep.method} />
          <code className="text-sm font-mono text-[#0B1B3D] truncate">
            {ep.path}
          </code>
          <span className="text-xs text-slate-500 truncate hidden md:inline">
            — {ep.title}
          </span>
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-100 p-4 space-y-3 bg-slate-50">
          <div className="text-sm text-[#0B1B3D] font-semibold">{ep.title}</div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
              cURL
            </div>
            <CodeBlock code={curl} testid={`copy-curl-${ep.path}`} />
          </div>
          {ep.response && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                Respuesta ejemplo
              </div>
              <CodeBlock code={ep.response} testid={`copy-resp-${ep.path}`} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ApiPage() {
  const { user } = useAuth();
  const token = typeof window !== "undefined" ? localStorage.getItem("exis_token") : null;
  const [openSection, setOpenSection] = useState("auth");

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto p-6 md:p-10">
        <PageHeader
          eyebrow="Desarrolladores"
          title="API"
          description="Documentación completa de la API REST de EXIS PHARMA. Integra tus sistemas para automatizar la carga de XMLs, descarga de SAP consolidado y consulta de entregas RDA."
        />

        {/* Overview */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <InfoCard
            label="Base URL"
            value={BASE_URL}
            mono
            testid="api-base-url"
          />
          <InfoCard label="Autenticación" value="JWT Bearer Token" />
          <InfoCard label="Formato" value="JSON / multipart / xlsx" />
        </div>

        {/* Your token */}
        <div className="bg-white border border-slate-200 mb-6">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
            <div className="w-9 h-9 bg-[#D95A40] text-white flex items-center justify-center">
              <KeyRound className="w-4 h-4" />
            </div>
            <div>
              <div className="font-display font-bold text-[#0B1B3D]">
                Tu token de acceso actual
              </div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-0.5">
                Válido 12 horas — usuario: {user?.email} ({user?.role})
              </div>
            </div>
          </div>
          <div className="p-4">
            {token ? (
              <CodeBlock code={token} testid="copy-my-token" />
            ) : (
              <div className="text-sm text-slate-500">
                Inicia sesión para ver tu token.
              </div>
            )}
            <div className="text-xs text-slate-500 mt-3">
              Usa este token en el header{" "}
              <code className="bg-slate-100 px-1.5 py-0.5">
                Authorization: Bearer &lt;token&gt;
              </code>{" "}
              de cada request.
            </div>
          </div>
        </div>

        {/* Quick example */}
        <div className="bg-white border border-slate-200 mb-6">
          <div className="px-5 py-4 border-b border-slate-200">
            <div className="font-display font-bold text-[#0B1B3D]">
              Ejemplo rápido
            </div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mt-0.5">
              Login + subir XML del mes actual
            </div>
          </div>
          <div className="p-4">
            <CodeBlock
              testid="copy-quick-example"
              code={`# 1) Login
TOKEN=$(curl -s -X POST '${BASE_URL}/api/auth/login' \\
  -H 'Content-Type: application/json' \\
  -d '{"email":"cliente@parthenon.cl","password":"Cliente2026!"}' \\
  | jq -r .token)

# 2) Subir XML al mes 2026-04
curl -X POST '${BASE_URL}/api/batches/upload' \\
  -H "Authorization: Bearer $TOKEN" \\
  -F 'files=@guia.xml' \\
  -F 'mes=2026-04' \\
  -F 'descripcion=Abril'

# 3) Descargar consolidado del mes
curl -X GET '${BASE_URL}/api/consolidado/download?month=2026-04' \\
  -H "Authorization: Bearer $TOKEN" \\
  -o Consolidado_202604.xlsx`}
            />
          </div>
        </div>

        {/* Endpoints by section */}
        <div className="space-y-4">
          {SECTIONS.map((s) => (
            <div key={s.key} className="bg-white border border-slate-200">
              <button
                onClick={() =>
                  setOpenSection(openSection === s.key ? "" : s.key)
                }
                data-testid={`api-section-${s.key}`}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 text-left"
              >
                <div className="flex items-center gap-3">
                  {openSection === s.key ? (
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-slate-500" />
                  )}
                  <div className="w-8 h-8 bg-[#0B1B3D] text-white flex items-center justify-center">
                    {s.icon}
                  </div>
                  <div>
                    <div className="font-display font-bold text-[#0B1B3D]">
                      {s.title}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">
                      {s.endpoints.length} endpoints
                    </div>
                  </div>
                </div>
              </button>
              {openSection === s.key && (
                <div className="border-t border-slate-100 p-4 space-y-3 bg-slate-50">
                  <p className="text-sm text-slate-600">{s.description}</p>
                  <div className="space-y-2">
                    {s.endpoints.map((ep, i) => (
                      <Endpoint key={i} ep={ep} token={token} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer link */}
        <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-t border-slate-200 pt-6">
          <div className="text-xs text-slate-500">
            La documentación OpenAPI interactiva también está disponible en:
          </div>
          <a
            href={`${BASE_URL}/docs`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="api-openapi-link"
            className="flex items-center gap-2 px-4 py-2 bg-[#0B1B3D] hover:bg-[#D95A40] text-white text-xs font-bold uppercase tracking-wider transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Abrir Swagger UI
          </a>
        </div>
      </div>
    </AppShell>
  );
}

function InfoCard({ label, value, mono, testid }) {
  return (
    <div
      className="bg-white border border-slate-200 p-4"
      data-testid={testid}
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">
        {label}
      </div>
      <div
        className={`text-sm text-[#0B1B3D] font-semibold ${
          mono ? "font-mono text-xs break-all" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}
